import { calculateNextBillingPeriodStartDate } from '@oskecare/fe-utilities/scripts/helpers';
import request from '@oskecare/fe-utilities/lib/request';
import {
  BILLING_ACCOUNT,
  CUSTOMER,
  INSTALLED_BASIC_PRODUCTS,
  MANAGE_USER_ACCOUNT,
  PROPERTIES,
} from '@oskecare/fe-utilities/lib/constants/dataSources';
import { cacheWithErrorCheck } from '../../helpers.js';
import BillingAccountInfo from './models/BillingAccountInfo.js';
import InstalledBasicProducts from './models/InstalledBasicProducts.js';
import CustomerData from './CustomerData/index.js';
import CustomerAccountInfo from './models/CustomerAccountInfo.js';

/**
 * CustomerDataManager module to handle processing of customer data and passing it to app via messageBus
 *
 * @property {AuthManager} _authManager
 * @property {Logger} _logger
 * @property {TagManager} _tagManager
 * @property {string} _customerApiUrl
 * @property {string} _authApiUrl
 * @property {string} _billingAccountApiUrl
 * @property {string} _customerInformationManagementApiUrl
 * @property {string} _subscriptionApiUrl
 * @property {string} _productDomainApiUrl
 * @property {MessageBus} _messageBus
 * @property {?Router} _router
 * @property {?string} _workingSn
 * @property {?CustomerData} _customerData
 * @property {Map<string, Promise<*>>} _cache
 */
export default class CustomerDataManager {
  /**
   * @param {MessageBus} messageBus
   * @param {Logger} logger
   * @param {AuthManager} authManager
   * @param {TagManager} tagManager
   * @param {object} config
   */
  constructor({ messageBus, logger, authManager, tagManager, config }) {
    this._authManager = authManager;
    this._logger = logger;
    this._tagManager = tagManager;
    this._customerApiUrl = config.customerApiUrl;
    this._authApiUrl = config.monicaApiUrl;
    this._billingAccountApiUrl = config.billingAccountApiUrl;
    this._customerInformationManagementApiUrl = config.customerInformationManagementApiUrl;
    this._subscriptionApiUrl = config.subscriptionApiUrl;
    this._productDomainApiUrl = config.productDomainApiUrl;
    this._messageBus = messageBus;
    this._router = null;
    this._workingSn = null;
    this._customerData = null;
    this._cache = new Map();
    this.changeWorkingSn = this.changeWorkingSn.bind(this);
    this.changeWorkingSnFromRoute = this.changeWorkingSnFromRoute.bind(this);
    this.getNextBillingPeriodStartDate = this.getNextBillingPeriodStartDate.bind(this);
    this.getIdentityList = this.getIdentityList.bind(this);
    this.getLinkWithContext = this.getLinkWithContext.bind(this);
  }

  /**
   * @calls { GET; {subscriptionApiUrl}; SUBSCRIPTION }
   * @calls { GET; {subscriptionApiUrl}/{subscriptionId}; SUBSCRIPTION }
   * @return {Promise<void>}
   */
  async initialize() {
    const customerData = await this.getCustomerData();
    this._workingSn = customerData.loggedInSnData.sn;
    await this._logger.attachCustomerDataManager(this);
  }

  /**
   * @calls { GET; {subscriptionApiUrl}; SUBSCRIPTION }
   * @calls { GET; {subscriptionApiUrl}/{subscriptionId}; SUBSCRIPTION }
   * @return {Promise<CustomerData>}
   */
  async getCustomerData() {
    // TODO: this method is only truly async during init, this logic could me moved to init and `getCustomerData` could be as synchronous getter
    if (!this._customerData) {
      const customerData = new CustomerData({
        userData: this._authManager.getUserData(),
        subscriptionApiUrl: this._subscriptionApiUrl,
        logSource: 'infra-customer-data-manager',
        isFatal: true,
      });
      await customerData.load();

      this._customerData = customerData;
    }
    return this._customerData;
  }

  /**
   * Use `getCustomerData` wherever possible. Ideally the use of this method will be refactored out.
   *
   * @return {CustomerData}
   */
  getCustomerDataSync() {
    if (!this._customerData) {
      throw new Error('Attempting to retrieve `customerData` synchronously before it was loaded.');
    }

    return this._customerData;
  }

  /**
   * To avoid cyclic dependency between the Router and CustomerDataManager, Router instance attaches
   * itself onto CustomerDataManager instance during initialization. This way we can use the single instance export pattern and still have both
   * instances invoking each other without a cyclic dependency.
   *
   * @param {Router} router
   * @see {Router.initialize}
   */
  attachRouter(router) {
    this._router = router;
  }

  /**
   * Check whether current customer is B2B with business type unsupported by ecare2
   * and redirect them to screen where they are told not all features are supported for them.
   *
   * Redirect is active every 30 days to remind customers they are not completely supported.
   *
   * @return {Promise<void>}
   */
  async checkIsB2BClient() {
    const localStorageObject = JSON.parse(localStorage.getItem('osk-b2b-accepted'));
    const isRedirectActive =
      !localStorageObject || new Date() >= Date.parse(localStorageObject.expiresAt);

    if (!isRedirectActive) {
      return;
    }

    const customerAccountInfo = await this.getCustomerAccountInfo(true).catch(() => null);

    if (customerAccountInfo.isEcareUnsupportedB2BCustomer) {
      this._router.navigate('/b2b');
    }
  }

  /**
   * Sets current workingSn as url param.
   *
   * @param {?string} workingSn
   * @param {URLSearchParams} query
   */
  changeWorkingSn({ workingSn, query }) {
    const loggedInSn = this.getCustomerDataSync().loggedInSnData.sn;

    if (workingSn !== loggedInSn) {
      query.set('sn', workingSn);
    } else {
      query.delete('sn');
    }

    if (workingSn !== null) {
      this._workingSn = workingSn;
    } else {
      this._workingSn = loggedInSn;
    }

    // TODO: replace `setQuery` RPC call with direct invocation when circular dependency is resolved
    this._messageBus.publish('router', 'setQuery', { query });

    this._tagManager.mhubPush({
      event: 'action.switchAccount.master',
      action: {
        switchAccount: {
          method: 'manual',
        },
      },
    });
  }

  /**
   * Get currently selected working SN
   *
   * @return {?string} - should only be null before initialization
   */
  get workingSn() {
    return this._workingSn;
  }

  /**
   * Get currently working SN is single SN customer
   *
   * @return {boolean}
   */
  get isSingleSn() {
    const customerData = this.getCustomerDataSync();
    return customerData.cnData.isCustomerAdmin && customerData.snDataList.length === 1;
  }

  /**
   * Proxy for `request` function with logSource pre-set
   *
   * @param {object} params
   * @return {Promise<*>}
   * @private
   */
  request(params) {
    return request({
      ...params,
      logSource: 'infra-customer-data-manager',
    }).catch(this._logger.catchError);
  }

  /**
   * Sets current _workingSn from message.
   * Used to change workingSn based on.
   *
   * @param {?string} workingSn
   */
  changeWorkingSnFromRoute({ workingSn }) {
    const loggedInSn = this.getCustomerDataSync().loggedInSnData.sn;

    if (workingSn !== this._workingSn) {
      if (workingSn !== null) {
        this._workingSn = workingSn;
      } else {
        this._workingSn = loggedInSn;
      }
      this._tagManager.mhubPush({
        event: 'action.switchAccount.master',
        action: {
          switchAccount: {
            method: 'automatic',
          },
        },
      });
    }
  }

  /**
   * Returns the starting date of the next billing period.
   *
   * @returns {Promise<string>}
   */
  async getNextBillingPeriodStartDate() {
    const billingAccountInfo = await this.getBillingAccountInfo();
    return calculateNextBillingPeriodStartDate(billingAccountInfo.billingPeriod);
  }

  /**
   * Fetch additional identities.
   *
   * @returns {Promise<{
   *  id: string,
   *  identNick: string,
   *  selectedIdentity: boolean,
   * }[]>}
   */
  getIdentityList() {
    return cacheWithErrorCheck(this._cache, 'identityList', () =>
      this.request({
        method: 'GET',
        route: `${this._authApiUrl}/identList`,
        dataSource: MANAGE_USER_ACCOUNT,
      }).then(data => data.identList),
    );
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<CustomerAccountInfo>}
   */
  async getCustomerAccountInfo(isNonessential = false) {
    const { customerId } = (await this.getCustomerData()).cnData;
    return cacheWithErrorCheck(this._cache, 'customerAccountInfo', () =>
      this.request({
        method: 'GET',
        route: `${this._customerApiUrl}/${customerId}`,
        dataSource: CUSTOMER,
        isNonessential,
      }).then(data => new CustomerAccountInfo(data)),
    );
  }

  /**
   * @param {string} email
   * @return {Promise<void>}
   */
  async setEmail(email) {
    const { customerId } = (await this.getCustomerData()).cnData;
    await this.request({
      method: 'PATCH',
      route: `${this._customerApiUrl}/${customerId}`,
      dataSource: CUSTOMER,
      useDefaultHeaders: false,
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Content-type': 'application/json-patch+json; charset=utf-8',
      },
      body: CustomerAccountInfo.createEmailPatchBody(email),
      isImportant: true,
    });
    this._cache.delete('customerAccountInfo');
  }

  /**
   * Create URL that specifies context/working SN.
   * If you don't specify SN, current working SN is assumed.
   * If desired context SN matches currently logged-in SN the link is not modified.
   *
   * @param {string} url
   * @param {?string} sn
   * @returns {string}
   */
  getLinkWithContext(url, sn = null) {
    const contextSn = sn ?? this.workingSn;
    if (contextSn === this.getCustomerDataSync().loggedInSnData.sn) {
      return url;
    }
    const [path, params] = url.split('?');
    const query = new URLSearchParams(params);
    query.set('sn', contextSn);
    return `${path}?${query}`;
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<BillingAccountInfo>}
   */
  getBillingAccountInfo(isNonessential = false) {
    return cacheWithErrorCheck(this._cache, 'billingAccountInfo', async () => {
      const { billingAccountId } = (await this.getCustomerData()).cnData;

      return this.request({
        method: 'GET',
        route: `${this._billingAccountApiUrl}/${billingAccountId}`,
        query: {
          returnPTPLimit: true,
        },
        headers: {
          'X-Varnish-Cache': 'forceUpdateCache',
        },
        dataSource: BILLING_ACCOUNT,
        isNonessential,
      }).then(data => new BillingAccountInfo(data));
    });
  }

  /**
   * @param {BillingAccountAddress} billingAccountAddress
   * @return {Promise<void>}
   */
  async setBillingAccountAddress(billingAccountAddress) {
    const { billingAccountId } = (await this.getCustomerData()).cnData;

    this._cache.delete('billingAccountInfo');
    return this.request({
      method: 'PATCH',
      route: `${this._billingAccountApiUrl}/${billingAccountId}`,
      dataSource: BILLING_ACCOUNT,
      useDefaultHeaders: false,
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Content-type': 'application/json-patch+json; charset=utf-8',
      },
      body: billingAccountAddress.createPatchBody(),
    });
  }

  /**
   *
   * @param {string} paymentMethod
   * @return {Promise<void>}
   */
  async setPaymentMethod(paymentMethod) {
    const { billingAccountId } = (await this.getCustomerData()).cnData;

    this._cache.delete('billingAccountInfo');
    return this.request({
      method: 'PATCH',
      route: `${this._billingAccountApiUrl}/${billingAccountId}`,
      dataSource: BILLING_ACCOUNT,
      useDefaultHeaders: false,
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Content-type': 'application/json-patch+json; charset=utf-8',
      },
      body: BillingAccountInfo.createPatchBody('defaultPaymentMethod', { id: paymentMethod }),
    });
  }

  /**
   * @param {number} limit
   * @return {Promise<*>}
   */
  async setPostpaidRefillLimit(limit) {
    const { billingAccountId } = (await this.getCustomerData()).cnData;

    this._cache.delete('billingAccountInfo');
    return this.request({
      method: 'PATCH',
      route: `${this._billingAccountApiUrl}/${billingAccountId}`,
      dataSource: BILLING_ACCOUNT,
      useDefaultHeaders: false,
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Content-type': 'application/json-patch+json; charset=utf-8',
      },
      body: BillingAccountInfo.createPatchBody('prepayBalanceRechargeMonthlyLimit', limit),
    });
  }

  /**
   * Checks whether customer has provided GAUSS property set.
   * Only a positively identified GAUSS property results in a `true`, `false` is assumed otherwise.
   *
   * @param {number} propertyId
   * @returns {Promise<boolean>}
   */
  async getIsGaussPropertySet(propertyId) {
    const data = await cacheWithErrorCheck(this._cache, `gaussProperty.${propertyId}`, async () => {
      const { billingAccountId } = (await this.getCustomerData()).cnData;

      return this.request({
        method: 'GET',
        route: `${this._customerInformationManagementApiUrl}/billingAccount/${billingAccountId}/properties?property.id=${propertyId}`,
        isNonessential: true,
        dataSource: PROPERTIES,
      })
        .then(response => response?.properties?.billingAccountProperties)
        .catch(() => null);
    });

    if (!data) {
      this._logger.error("Failed to retrieve gauss property, assuming it's not set.");
      return false;
    }

    return data.property?.length === 1 && data.property[0]?.type === propertyId;
  }

  /**
   * @param {string} subscriptionId
   * @param {boolean} isNonessential
   * @return {Promise<InstalledBasicProducts>}
   */
  getInstalledBasicProducts(subscriptionId, isNonessential = false) {
    return cacheWithErrorCheck(this._cache, `installedBasicProduct.${subscriptionId}`, () =>
      this.request({
        method: 'GET',
        route: `${this._productDomainApiUrl}/subscription/${subscriptionId}/installedBasicProducts`,
        dataSource: INSTALLED_BASIC_PRODUCTS,
        isNonessential,
      }).then(data => new InstalledBasicProducts(data)),
    );
  }

  /**
   * @param {boolean} isNonessential
   * @returns {Promise<{
   *   type: string,
   *   id: string,
   *   customerNumber: string,
   *   created: string,
   *   status: string,
   * }[]>}
   */
  getPaymentMethodChangeRequests(isNonessential = false) {
    return cacheWithErrorCheck(this._cache, 'paymentMethod.changeRequests', () =>
      this.request({
        method: 'GET',
        route: `${this._billingAccountApiUrl}/${this._customerData.cnData.billingAccountId}/paymentMethod/changeRequests`,
        dataSource: BILLING_ACCOUNT,
        isNonessential,
      }),
    );
  }

  resetPaymentMethodChangeRequestsCache() {
    this._cache.delete('paymentMethod.changeRequests');
  }
}
